/**
 * Immutably sets a value inside an object. Like `lodash#set`, but returning a
 * new object. Treats nullish initial values as empty objects. Clones any
 * nested objects. Supports arrays, too.
 *
 * @param {Object}              object Object to set a value in.
 * @param {number|string|Array} path   Path in the object to modify.
 * @param {*}                   value  New value to set.
 * @return {Object} Cloned object with the new value set.
 */
export function setImmutably<
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	T extends Array< any > | Record< string | number, any >,
>(
	object: T,
	path: string | ReadonlyArray< string >,
	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
	value: any
): Readonly< T > {
	// Normalize path
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const keys = Array.isArray( path ) ? [ ...path ] : [ path ];

	// Shallowly clone the base of the object
	object = Array.isArray( object ) ? ( [ ...object ] as T ) : { ...object };

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const leaf = keys.pop();

	// Traverse object from root to leaf, shallowly cloning at each level
	let prev = object;
	for ( const key of keys ) {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
		const lvl = prev[ key ];
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
		prev = prev[ key ] = Array.isArray( lvl ) ? [ ...lvl ] : { ...lvl };
	}

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
	prev[ leaf ] = value;

	return object;
}

/**
 * Helper util to return a value from a certain path of the object.
 * Path is specified as either:
 * - a string of properties, separated by dots, for example: "x.y".
 * - an array of properties, for example `[ 'x', 'y' ]`.
 * You can also specify a default value in case the result is nullish.
 *
 * @param {Object}       object       Input object.
 * @param {string|Array} path         Path to the object property.
 * @param {*}            defaultValue Default value if the value at the specified path is nullish.
 * @return {*} Value of the object property at the specified path.
 */
export function getValueFromObjectPath< T = unknown >(
	object: Record< string, unknown >,
	path: string | Array< string >,
	defaultValue?: T
): T {
	const arrayPath = Array.isArray( path ) ? path : path.split( '.' );
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let value: any = object;
	arrayPath.forEach( ( fieldName ) => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
		value = value?.[ fieldName ];
	} );
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return value ?? defaultValue;
}

/**
 * Helper util to filter out objects with duplicate values for a given property.
 *
 * @param {Object[]} array    Array of objects to filter.
 * @param {string}   property Property to filter unique values by.
 *
 * @return {Object[]} Array of objects with unique values for the specified property.
 */
export function uniqByProperty< T extends Record< string, unknown > >(
	array: T[],
	property: keyof T
): T[] {
	const seen = new Set();
	return array.filter( ( item ) => {
		const value = item[ property ];
		return seen.has( value ) ? false : seen.add( value );
	} );
}
